// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Fiscal/EmissaoNfce.tsx
//
//  Página de emissão de NFC-e (modelo 65). Foco em uso de PDV: venda
//  presencial a consumidor, identificação opcional até R$ 10k.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Plus, Trash2, Send, Printer, RefreshCw, AlertTriangle, CheckCircle2, QrCode,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useEmitirNfce, useProximoNumeroNfce, useObterDanfeNfce } from '@/features/fiscal/hooks/useNfce';
import { FORMAS_PAGAMENTO_NFE } from '@/features/fiscal/types';
import type {
  EmitirNfceRequest, EmitirNfceResponse, NfceItemRequest, NfcePagamentoRequest,
} from '@/features/fiscal/types';

// ── helpers ───────────────────────────────────────────────────────────────────

const moeda = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const novoItem = (): NfceItemRequest => ({
  produtoId:          0,
  descricao:          '',
  ncm:                '00000000',
  cfop:               '5102',
  csosn:              '102',
  unidade:            'UN',
  quantidade:         1,
  valorUnitario:      0,
  desconto:           0,
  baseIcms:           0,
  aliqIcms:           0,
  valorIcms:          0,
  aliqPis:            0,
  valorPis:           0,
  aliqCofins:         0,
  valorCofins:        0,
  valorAproxTributos: 0,
});

const novoPagamento = (): NfcePagamentoRequest => ({
  formaPagamento: '01',
  valor:          0,
});

// ══════════════════════════════════════════════════════════════════════════════
//  Página
// ══════════════════════════════════════════════════════════════════════════════

const EmissaoNfce = () => {
  const proximoNumero = useProximoNumeroNfce();
  const emitir        = useEmitirNfce();
  const obterDanfe    = useObterDanfeNfce();

  const [itens, setItens] = useState<NfceItemRequest[]>([novoItem()]);
  const [pagamentos, setPagamentos] = useState<NfcePagamentoRequest[]>([novoPagamento()]);
  const [consumidor, setConsumidor] = useState({
    cpfCnpj: '',
    nome:    '',
    email:   '',
  });
  const [indPresenca, setIndPresenca] = useState(1);
  const [obsContrib, setObsContrib]   = useState('');

  // Resultado da última emissão (mostra modal pós-emissão)
  const [ultimaEmissao, setUltimaEmissao] = useState<EmitirNfceResponse | null>(null);

  const totalProdutos = itens.reduce(
    (acc, i) => acc + i.quantidade * i.valorUnitario - i.desconto, 0,
  );
  const totalPagamentos = pagamentos.reduce((acc, p) => acc + p.valor, 0);

  // Auto-ajusta o valor do primeiro pagamento ao total da nota
  useEffect(() => {
    if (pagamentos.length === 1 && pagamentos[0].valor === 0) {
      setPagamentos([{ ...pagamentos[0], valor: totalProdutos }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalProdutos]);

  const acima10k = totalProdutos >= 10000;
  const consumidorObrigatorio = acima10k && !consumidor.cpfCnpj;

  const setItem = (idx: number, patch: Partial<NfceItemRequest>) =>
    setItens((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removerItem = (idx: number) =>
    setItens((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const setPagamento = (idx: number, patch: Partial<NfcePagamentoRequest>) =>
    setPagamentos((arr) => arr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removerPagamento = (idx: number) =>
    setPagamentos((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const validar = (): string | null => {
    if (itens.length === 0)                    return 'Adicione pelo menos um item.';
    if (itens.some((i) => !i.descricao.trim())) return 'Todos os itens precisam ter descrição.';
    if (itens.some((i) => i.quantidade <= 0))   return 'Quantidade deve ser maior que zero em todos os itens.';
    if (itens.some((i) => i.valorUnitario <= 0))return 'Valor unitário deve ser maior que zero.';
    if (consumidorObrigatorio)                  return 'Acima de R$ 10.000 é obrigatório informar CPF/CNPJ do consumidor.';
    if (Math.abs(totalPagamentos - totalProdutos) > 0.01)
      return `Soma dos pagamentos (${moeda(totalPagamentos)}) difere do total (${moeda(totalProdutos)}).`;
    return null;
  };

  const handleEmitir = async () => {
    const erro = validar();
    if (erro) {
      // eslint-disable-next-line no-alert
      alert(erro);
      return;
    }

    const req: EmitirNfceRequest = {
      cpfCnpjConsumidor: consumidor.cpfCnpj || undefined,
      nomeConsumidor:    consumidor.nome || undefined,
      emailConsumidor:   consumidor.email || undefined,
      indicadorPresenca: indPresenca,
      naturezaOperacao:  'VENDA AO CONSUMIDOR FINAL',
      valorFrete:        0,
      valorSeguro:       0,
      valorDesconto:     0,
      obsContrib:        obsContrib || undefined,
      itens,
      formasPagamento:   pagamentos,
    };

    const res = await emitir.mutateAsync(req);
    setUltimaEmissao(res);

    if (res.sucesso) {
      // Reseta o formulário para próxima emissão
      setItens([novoItem()]);
      setPagamentos([novoPagamento()]);
      setConsumidor({ cpfCnpj: '', nome: '', email: '' });
      setObsContrib('');
      proximoNumero.refetch();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Emissão de NFC-e</h1>
          <p className="text-muted-foreground mt-1">
            Modelo 65 — venda presencial a consumidor final.
          </p>
        </div>

        <Card className="min-w-[260px]">
          <CardContent className="pt-4 pb-3">
            {proximoNumero.isLoading
              ? <Skeleton className="h-6 w-full" />
              : (
                <div className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Próximo número</span>
                    <span className="font-semibold">
                      {proximoNumero.data?.proximoNumero ?? '—'} / Série {proximoNumero.data?.serie ?? '—'}
                    </span>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Identificação do consumidor */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identificação do Consumidor</CardTitle>
          <CardDescription>
            Opcional até R$ 10.000,00. Acima disso, CPF/CNPJ é obrigatório.
            {acima10k && !consumidor.cpfCnpj && (
              <span className="inline-flex items-center gap-1 ml-2 text-yellow-600 font-medium">
                <AlertTriangle className="h-3 w-3" />
                NF acima de R$ 10k — exige CPF/CNPJ
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>CPF / CNPJ</Label>
            <Input
              value={consumidor.cpfCnpj}
              onChange={(e) => setConsumidor((c) => ({ ...c, cpfCnpj: e.target.value }))}
              placeholder="Apenas dígitos"
              maxLength={14}
            />
          </div>
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input
              value={consumidor.nome}
              onChange={(e) => setConsumidor((c) => ({ ...c, nome: e.target.value }))}
              placeholder="CONSUMIDOR"
              maxLength={60}
            />
          </div>
          <div className="space-y-1">
            <Label>E-mail (envio do XML)</Label>
            <Input
              type="email"
              value={consumidor.email}
              onChange={(e) => setConsumidor((c) => ({ ...c, email: e.target.value }))}
              maxLength={80}
            />
          </div>
          <div className="space-y-1 md:col-span-3 max-w-[300px]">
            <Label>Indicador de Presença</Label>
            <Select value={String(indPresenca)} onValueChange={(v) => setIndPresenca(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 — Presencial (PDV)</SelectItem>
                <SelectItem value="2">2 — Internet</SelectItem>
                <SelectItem value="3">3 — Teleatendimento</SelectItem>
                <SelectItem value="4">4 — Entrega em domicílio</SelectItem>
                <SelectItem value="5">5 — Presencial fora do estabelecimento</SelectItem>
                <SelectItem value="9">9 — Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Itens</CardTitle>
            <CardDescription>{itens.length} item(s)</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => setItens([...itens, novoItem()])}>
            <Plus className="h-4 w-4 mr-1" />Adicionar item
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Código</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="w-[80px]">NCM</TableHead>
                  <TableHead className="w-[70px]">CFOP</TableHead>
                  <TableHead className="w-[60px]">Un.</TableHead>
                  <TableHead className="w-[80px] text-right">Qtd</TableHead>
                  <TableHead className="w-[100px] text-right">Vl. Unit.</TableHead>
                  <TableHead className="w-[100px] text-right">Desc.</TableHead>
                  <TableHead className="w-[110px] text-right">Total</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.produtoId || ''}
                        onChange={(e) => setItem(idx, { produtoId: Number(e.target.value) })}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.descricao}
                        onChange={(e) => setItem(idx, { descricao: e.target.value })}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.ncm}
                        onChange={(e) => setItem(idx, { ncm: e.target.value })}
                        maxLength={8}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.cfop}
                        onChange={(e) => setItem(idx, { cfop: e.target.value })}
                        maxLength={6}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.unidade}
                        onChange={(e) => setItem(idx, { unidade: e.target.value })}
                        maxLength={6}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.001"
                        value={item.quantidade || ''}
                        onChange={(e) => setItem(idx, { quantidade: Number(e.target.value) })}
                        className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.valorUnitario || ''}
                        onChange={(e) => setItem(idx, { valorUnitario: Number(e.target.value) })}
                        className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.desconto || ''}
                        onChange={(e) => setItem(idx, { desconto: Number(e.target.value) })}
                        className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {moeda(item.quantidade * item.valorUnitario - item.desconto)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removerItem(idx)}
                        disabled={itens.length === 1}
                        className="h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end mt-3 text-sm">
            <div className="font-semibold">
              Total dos itens: <span className="ml-2">{moeda(totalProdutos)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pagamentos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Formas de Pagamento</CardTitle>
            <CardDescription>
              Soma deve igualar o total dos itens ({moeda(totalProdutos)}).
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => setPagamentos([...pagamentos, novoPagamento()])}>
            <Plus className="h-4 w-4 mr-1" />Adicionar pagamento
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {pagamentos.map((p, idx) => (
              <div key={idx} className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Forma</Label>
                  <Select
                    value={p.formaPagamento}
                    onValueChange={(v) => setPagamento(idx, { formaPagamento: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMAS_PAGAMENTO_NFE.map((f) => (
                        <SelectItem key={f.codigo} value={f.codigo}>
                          {f.codigo} — {f.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[150px] space-y-1">
                  <Label className="text-xs">Valor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={p.valor || ''}
                    onChange={(e) => setPagamento(idx, { valor: Number(e.target.value) })}
                    className="text-right"
                  />
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removerPagamento(idx)}
                  disabled={pagamentos.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-3 text-sm">
            <div className={`font-semibold ${
              Math.abs(totalPagamentos - totalProdutos) > 0.01 ? 'text-destructive' : ''
            }`}>
              Total dos pagamentos: <span className="ml-2">{moeda(totalPagamentos)}</span>
              {Math.abs(totalPagamentos - totalProdutos) > 0.01 && (
                <span className="ml-2 text-xs">
                  (diferença: {moeda(totalPagamentos - totalProdutos)})
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Observação */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Observação ao Contribuinte</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={obsContrib}
            onChange={(e) => setObsContrib(e.target.value)}
            placeholder="Texto livre — aparece no rodapé do DANFE."
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-2">
        <Button
          size="lg"
          className="gap-2"
          onClick={handleEmitir}
          disabled={emitir.isPending}
        >
          {emitir.isPending
            ? <><RefreshCw className="h-4 w-4 animate-spin" />Transmitindo...</>
            : <><Send className="h-4 w-4" />Emitir NFC-e</>}
        </Button>
      </div>

      {/* Modal de resultado */}
      <Dialog
        open={ultimaEmissao !== null}
        onOpenChange={(o) => { if (!o) setUltimaEmissao(null); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {ultimaEmissao?.sucesso
                ? <><CheckCircle2 className="h-5 w-5 text-green-500" />NFC-e Autorizada</>
                : <><AlertTriangle className="h-5 w-5 text-destructive" />Rejeitada pela SEFAZ</>}
            </DialogTitle>
            <DialogDescription>
              {ultimaEmissao && (
                <>
                  <Badge className="mr-2">{ultimaEmissao.cStat}</Badge>
                  {ultimaEmissao.xMotivo}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {ultimaEmissao?.sucesso && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Número:</span>{' '}
                  <span className="font-semibold">{ultimaEmissao.numeroNota}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Chave:</span>{' '}
                  <span className="font-mono text-xs break-all">{ultimaEmissao.chaveNota}</span>
                </div>
                {ultimaEmissao.protocolo && (
                  <div>
                    <span className="text-muted-foreground">Protocolo:</span>{' '}
                    <span className="font-mono text-xs">{ultimaEmissao.protocolo}</span>
                  </div>
                )}
              </div>

              {ultimaEmissao.qrCode && (
                <div className="flex flex-col items-center gap-2 p-4 border rounded-md">
                  <QrCode className="h-4 w-4 text-muted-foreground" />
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(ultimaEmissao.qrCode)}`}
                    alt="QR Code NFC-e"
                    width={240}
                    height={240}
                    className="rounded"
                  />
                  {ultimaEmissao.urlConsulta && (
                    <a
                      href={ultimaEmissao.urlConsulta}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      Consultar na SEFAZ →
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {ultimaEmissao?.sucesso && (
              <Button
                variant="outline"
                onClick={() => obterDanfe.mutate(ultimaEmissao.notaEletronicaId)}
                disabled={obterDanfe.isPending}
                className="gap-2"
              >
                {obterDanfe.isPending
                  ? <><RefreshCw className="h-4 w-4 animate-spin" />Gerando...</>
                  : <><Printer className="h-4 w-4" />Imprimir DANFE</>}
              </Button>
            )}
            <Button onClick={() => setUltimaEmissao(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmissaoNfce;
